import { useEffect, useState, createContext, useContext } from "react";
import { media } from "../../assets/media";
import { projects } from "../../assets/projects";

const isImage = (src) => /\.(jpe?g|png|webp|svg)$/i.test(src);

const isVideo = (src) => /\.(mp4|webm)$/i.test(src);

// Every image is downloaded exactly once (as a blob) while the loading
// screen is up, and components render the resulting local object URL via
// getMediaSrc. After preload, showing an image never touches the network —
// not even a cache revalidation — so hover content appears instantly.
const blobUrls = new Map();

export const getMediaSrc = (src) => blobUrls.get(src) || src;

const preloadImage = async (src) => {
  try {
    const response = await fetch(src);
    const blob = await response.blob();
    blobUrls.set(src, URL.createObjectURL(blob));
  } catch {
    // component falls back to the original URL
  }
};

// document.fonts.ready resolves as soon as there are no pending font loads —
// and since the app (the only text using these fonts) isn't rendered until
// the loader goes away, no load is ever pending. fonts.load() forces the
// actual downloads so we genuinely wait for them.
const loadFonts = () =>
  Promise.all(
    ["Genos", "GenosBlack", "NicoMoji", "Monsieur"].map((family) =>
      document.fonts.load(`1rem ${family}`),
    ),
  ).catch(() => {});

// Fetch videos one by one, in the order they appear in the projects table,
// after the loader is gone. They land in the HTTP cache so hover playback
// starts instantly. Does not gate the loading screen.
const warmUpVideos = async () => {
  const videos = projects
    .flatMap((project) => project.mediaFiles)
    .filter((src) => src && isVideo(src));
  for (const src of videos) {
    try {
      await fetch(src, { cache: "force-cache" });
    } catch {
      // best-effort warm-up; hover will just load on demand
    }
  }
};

const PreloadContext = createContext({ isFontsReady: false, isMediaReady: false });
export const usePreload = () => useContext(PreloadContext);

export const PreloadProvider = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [isFontsReady, setIsFontsReady] = useState(false);
  const [isMediaReady, setIsMediaReady] = useState(false);

  useEffect(() => {
    Promise.all([
      loadFonts(),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]).then(() => setIsFontsReady(true));

    const sources = Object.values(media);
    const totalFiles = sources.length;
    let loaded = 0;

    const handleFileLoaded = () => {
      loaded = Math.min(loaded + 1, totalFiles);
      setProgress((loaded / totalFiles) * 100);
      if (loaded === totalFiles) {
        setIsMediaReady(true);
        warmUpVideos();
      }
    };

    sources.forEach((src) => {
      if (src && isImage(src)) preloadImage(src).then(handleFileLoaded);
      else handleFileLoaded();
    });
  }, []);

  const isReady = isFontsReady && isMediaReady;

  return (
    <PreloadContext.Provider value={{ isFontsReady, isMediaReady }}>
      {!isReady && (
        <div className="loading-screen">
          <div className="progress-bar">
            <div
              className="progress"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <p>Loading... {Math.round(Math.min(progress, 100))}%</p>
        </div>
      )}
      {isReady && children}
    </PreloadContext.Provider>
  );
};
